import Decimal from 'decimal.js';
import { EquipmentExtType } from '../contexts/types';
import GoogleAPI from '../utils/GoogleAPI';
import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.EquipmentStaffing.spreadsheet';

/**
 * 設備・人員構成変更データ変換結果
 */
export interface EquipmentStaffingConversionResult {
  data: EquipmentExtType[];
  errors: string[];
}

/**
 * 設備・人員構成変更データをSpreadSheetデータに変換
 * @param equipmentAmounts 設備リスト
 * @returns SpreadSheet用の2次元配列
 */
export const equipmentStaffingToSpreadSheetData = (equipmentAmounts: EquipmentExtType[]): any[][] => {
  // ヒント行（1行目）
  const hint = [i18n.t(`${T_PREFIX}.hint`), "", "", "", "", "", ""];

  // ヘッダー行（2行目）
  const header = [
    i18n.t(`${T_PREFIX}.header.enabled`),
    i18n.t(`${T_PREFIX}.header.equipmentName`),
    i18n.t(`${T_PREFIX}.header.unitPrice`),
    i18n.t(`${T_PREFIX}.header.quantity`),
    i18n.t(`${T_PREFIX}.header.amount`),
    i18n.t(`${T_PREFIX}.header.numberOfPeopleRequiredForEquipment`),
    i18n.t(`${T_PREFIX}.header.numberOfPeopleRequiredForWithoutEquipment`),
  ];

  const enabledLabel = i18n.t(`${T_PREFIX}.enabledValue.enabled`);
  const disabledLabel = i18n.t(`${T_PREFIX}.enabledValue.disabled`);

  // データ行（3行目から）
  const dataRows = equipmentAmounts.map((e, index) => {
    const row = index + 3; // ヒント1行 + ヘッダー1行 なので、データは3行目から
    return [
      e.selected ? enabledLabel : disabledLabel,
      `${e.equipmentName}（${e.equipmentId}）`,
      e.unitPrice ?? 0,
      e.changeInQuantity ?? 0,
      `=C${row}*D${row}`, // 金額 = 単価 × 数量（数式）
      e.numberOfPeopleRequiredForEquipment ?? 0,
      e.numberOfPeopleRequiredForWithoutEquipment ?? 0,
    ];
  });

  // 合計行
  const lastDataRow = equipmentAmounts.length + 2; // ヒント1行 + ヘッダー1行 + データ行数
  const beforeRow = lastDataRow + 1;
  const afterRow = lastDataRow + 2;

  const summaryRows = [
    ["", "", "", i18n.t(`${T_PREFIX}.summaryLabel.before`), `=SUM(E3:E${lastDataRow})`, "", ""],
    ["", "", "", i18n.t(`${T_PREFIX}.summaryLabel.after`), `=SUMIF(A3:A${lastDataRow},"${enabledLabel}",E3:E${lastDataRow})`, "", ""],
    ["", "", "", i18n.t(`${T_PREFIX}.summaryLabel.changeRate`), `=IF(E${beforeRow}=0,"0%",TEXT(INT((1-(E${afterRow}/E${beforeRow}))*-1*100),"0")&"%")`, "", ""],
  ];

  return [hint, header, ...dataRows, ...summaryRows];
};

/**
 * SpreadSheetデータを設備・人員構成変更データに変換
 * データ連携で連携する項目: 有効/無効、数量
 * @param values SpreadSheetから取得した2次元配列（ヒント行・ヘッダー行を含まない、A3:G から取得）
 * @param existingData 既存の設備リスト
 * @returns 変換結果（データ + エラー）
 */
export const spreadSheetDataToEquipmentStaffing = (
  values: any[][],
  existingData: EquipmentExtType[]
): EquipmentStaffingConversionResult => {
  if (!values || values.length === 0) {
    return { data: [], errors: [] };
  }

  // 既存データのコピーを作成
  const updatedData = existingData.map(e => ({ ...e }));
  const errors: string[] = [];

  // 各行を処理（合計行はスキップ）
  for (let i = 0; i < values.length; i++) {
    const row = values[i];
    if (!row || row.length === 0) continue;

    // D列（index 3）が合計行ラベル（変更前・変更後・増減率）の場合は合計行なのでスキップ
    const dColValue = row[3]?.toString().trim() ?? "";
    const summaryLabels = [
      i18n.t(`${T_PREFIX}.summaryLabel.before`),
      i18n.t(`${T_PREFIX}.summaryLabel.after`),
      i18n.t(`${T_PREFIX}.summaryLabel.changeRate`),
    ];
    if (summaryLabels.includes(dColValue)) {
      continue;
    }

    // A列が空の場合もスキップ（合計行の空行等）
    const enabledValue = row[0]?.toString().trim() ?? "";
    if (enabledValue === "") continue;

    const enabledLabel = i18n.t(`${T_PREFIX}.enabledValue.enabled`);
    const disabledLabel = i18n.t(`${T_PREFIX}.enabledValue.disabled`);

    // A列: 有効/無効以外の値の場合はエラー記録
    if (enabledValue !== enabledLabel && enabledValue !== disabledLabel) {
      errors.push(i18n.t(`${T_PREFIX}.message.invalidEnabledValue`, { row: i + 1, value: enabledValue }));
    }

    // B列: 設備名（設備ID）から設備IDを抽出（特定できない場合はエラー）
    const equipmentNameWithId = row[1]?.toString() ?? "";
    const idMatch = equipmentNameWithId.match(/.*（(.+)）$/);
    const equipmentId = idMatch ? idMatch[1] : null;
    if (!equipmentId) {
      if (equipmentNameWithId) {
        errors.push(i18n.t(`${T_PREFIX}.message.equipmentNotIdentified`, { row: i + 1, value: equipmentNameWithId }));
      }
      continue;
    }

    // 既存データから該当する設備を検索（見つからない場合はエラー）
    const targetIndex = updatedData.findIndex(e => e.equipmentId === equipmentId);
    if (targetIndex === -1) {
      errors.push(i18n.t(`${T_PREFIX}.message.equipmentNotFound`, { row: i + 1, equipmentId }));
      continue;
    }

    // 有効/無効の変換（「有効」以外はすべて無効として扱う）
    updatedData[targetIndex].selected = enabledValue === enabledLabel;

    // D列: 数量の変換（不正値は0として連携）
    const quantityValue = row[3];
    if (quantityValue !== null && quantityValue !== undefined && quantityValue !== "") {
      const num = Number(quantityValue);
      if (isNaN(num)) {
        errors.push(i18n.t(`${T_PREFIX}.message.quantityInvalid`, { equipmentName: updatedData[targetIndex].equipmentName, value: quantityValue }));
      } else if (num < 0) {
        errors.push(i18n.t(`${T_PREFIX}.message.quantityNegative`, { equipmentName: updatedData[targetIndex].equipmentName, value: quantityValue }));
      }
      const quantity = (isNaN(num) || num < 0) ? 0 : Math.floor(num);
      updatedData[targetIndex].changeInQuantity = quantity;
      // 金額 = 単価 × 数量で再計算
      const newAmount = new Decimal(updatedData[targetIndex].unitPrice || 0).mul(quantity).toString();
      updatedData[targetIndex].amount = newAmount;
    }
  }

  return { data: updatedData, errors };
};

/**
 * 設備・人員構成変更シートの入力規則・背景色を設定
 * データ書き込み後に呼び出す共通処理
 * @param googleAPI GoogleAPIインスタンス
 * @param spreadsheetId SpreadSheet ID
 * @param sheetId シートID
 * @param equipmentAmounts 設備リスト
 */
export const setupEquipmentStaffingSheet = async (
  googleAPI: GoogleAPI,
  spreadsheetId: string,
  sheetId: number,
  equipmentAmounts: EquipmentExtType[]
): Promise<void> => {
  const dataRowCount = equipmentAmounts.length;
  if (dataRowCount === 0) return;

  const enabledLabel = i18n.t(`${T_PREFIX}.enabledValue.enabled`);
  const disabledLabel = i18n.t(`${T_PREFIX}.enabledValue.disabled`);

  // 入力規則の設定（有効/無効プルダウン、設備名プルダウン）
  await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [
    {
      sheetId,
      startRow: 3,
      endRow: 2 + dataRowCount,
      startCol: 1,
      endCol: 1,
      values: [enabledLabel, disabledLabel],
      strict: true,
      showDropdown: true,
    },
    {
      sheetId,
      startRow: 3,
      endRow: 2 + dataRowCount,
      startCol: 2,
      endCol: 2,
      values: equipmentAmounts.map(e => `${e.equipmentName}（${e.equipmentId}）`),
      strict: true,
      showDropdown: true,
    },
  ]);

  // 編集対象外の列に薄い灰色の背景色を設定
  // B:設備名(2), C:単価(3), E:金額(5), F:設備に必要な人員数(6), G:設備なし時に必要な人員数(7)
  const readOnlyBgColor = { red: 0.93, green: 0.93, blue: 0.93 };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 2, endCol: 2, format: { backgroundColor: readOnlyBgColor } },
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 3, endCol: 3, format: { backgroundColor: readOnlyBgColor } },
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 5, endCol: 5, format: { backgroundColor: readOnlyBgColor } },
    { sheetId, startRow: 3, endRow: 2 + dataRowCount, startCol: 6, endCol: 7, format: { backgroundColor: readOnlyBgColor } },
  ]);
};
